import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip
} from '@material-ui/core';
import { Add, Edit, Delete, Check, Close } from '@material-ui/icons';
import { solicitudDetalleService } from '../../services';

const tipoOptions = [
  { value: 'CUENTA_CORRIENTE', label: 'Cuenta Corriente' },
  { value: 'CAJA_AHORRO', label: 'Caja de Ahorro' },
  { value: 'CUENTA_JUDICIAL', label: 'Cuenta Judicial' },
  { value: 'OTROS', label: 'Otros' }
];

const monedaOptions = [
  { value: 'PESOS', label: 'Pesos' },
  { value: 'DOLARES', label: 'Dólares' }
];

const tipoClaveOptions = [
  { value: 'CBU', label: 'CBU' },
  { value: 'CVU', label: 'CVU' }
];

const initialCuenta = {
  tipo: 'CUENTA_CORRIENTE',
  banco: '',
  moneda: 'PESOS',
  tipoClaveBancaria: 'CBU',
  claveBancaria: ''
};

const getTipoDisplay = (tipo) => {
  const map = {
    CUENTA_CORRIENTE: 'Cuenta Corriente',
    CAJA_AHORRO: 'Caja de Ahorro',
    CUENTA_JUDICIAL: 'Cuenta Judicial',
    OTROS: 'Otros'
  };
  return map[tipo] || tipo;
};

const getMonedaDisplay = (moneda) => {
  const map = {
    PESOS: 'Pesos',
    DOLARES: 'Dólares',
    BIMONETARIA: 'Bimonetaria'
  };
  return map[moneda] || moneda;
};

const getClaveDisplay = (tipo) => {
  const map = {
    CBU: 'CBU',
    CVU: 'CVU'
  };
  return map[tipo] || tipo;
};

const SeccionCuentasBancarias = ({ solicitudId, tipo }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [formData, setFormData] = useState(initialCuenta);
  const [claveBancariaError, setClaveBancariaError] = useState('');
  const [formVisible, setFormVisible] = useState(false);

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const getMethod = tipo === 'individuo'
        ? solicitudDetalleService.getCuentasBancariasIndividuo
        : solicitudDetalleService.getCuentasBancariasEmpresa;
      const response = await getMethod(solicitudId);
      if (response) {
        setCuentasBancarias(response.cuentasBancarias || []);
      }
    } catch (err) {
      setError('Error al cargar las cuentas bancarias');
    } finally {
      setLoading(false);
    }
  }, [solicitudId, tipo]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const validateClaveBancaria = (tipoClave, valor) => {
    if (!valor) return '';
    const soloNumeros = valor.replace(/[^0-9]/g, '');

    if (tipoClave === 'CBU' || tipoClave === 'CVU') {
      if (soloNumeros.length !== 22) {
        return `${tipoClave} debe tener exactamente 22 dígitos`;
      }
    }
    return '';
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    if (field === 'claveBancaria') {
      setClaveBancariaError(validateClaveBancaria(formData.tipoClaveBancaria, value));
    } else if (field === 'tipoClaveBancaria') {
      setClaveBancariaError(validateClaveBancaria(value, formData.claveBancaria));
    }
  };

  const isFormValid = () => {
    const requeridos = ['tipo', 'banco', 'moneda', 'tipoClaveBancaria', 'claveBancaria'];
    const completos = requeridos.every((campo) => {
      const value = formData[campo];
      return value !== undefined && value !== null && value.toString().trim() !== '';
    });
    return completos && !claveBancariaError;
  };

  const handleOpenForm = (index = -1) => {
    if (index >= 0) {
      const cuenta = cuentasBancarias[index];
      setFormData({
        tipo: cuenta.tipo,
        banco: cuenta.banco,
        moneda: cuenta.moneda,
        tipoClaveBancaria: cuenta.tipoClaveBancaria,
        claveBancaria: cuenta.claveBancaria
      });
      setEditingIndex(index);
    } else {
      setFormData(initialCuenta);
      setEditingIndex(-1);
    }
    setClaveBancariaError('');
    setFormVisible(true);
  };

  const handleCloseForm = () => {
    setEditingIndex(-1);
    setClaveBancariaError('');
    setFormData(initialCuenta);
    setFormVisible(false);
  };

  const handleSaveCuenta = () => {
    if (!isFormValid()) {
      setError('Por favor complete todos los campos requeridos.');
      return;
    }

    const nuevasCuentas = [...cuentasBancarias];

    if (editingIndex >= 0) {
      const cuentaExistente = cuentasBancarias[editingIndex];
      nuevasCuentas[editingIndex] = {
        ...cuentaExistente,
        ...formData,
        id: cuentaExistente.id
      };
    } else {
      nuevasCuentas.push({ ...formData });
    }

    setCuentasBancarias(nuevasCuentas);
    handleCloseForm();
    setError('');
  };

  const handleDeleteCuenta = (index) => {
    setCuentasBancarias(cuentasBancarias.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    if (cuentasBancarias.length === 0) {
      setError('Debe registrar al menos una cuenta bancaria.');
      setSaving(false);
      return;
    }

    try {
      const datosParaGuardar = {
        solicitudId: parseInt(solicitudId),
        cuentasBancarias
      };
      const saveMethod = tipo === 'individuo'
        ? solicitudDetalleService.saveCuentasBancariasIndividuo
        : solicitudDetalleService.saveCuentasBancariasEmpresa;
      await saveMethod(datosParaGuardar);
      setSuccess('Datos guardados exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Cuentas Bancarias
        </Typography>
        {error && (
          <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}
        {success && (
          <Box p={2} mb={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
            <Typography variant="body2">{success}</Typography>
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: '2rem' }}>
            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
              Mis Cuentas Bancarias
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenForm()}
              size="small"
              color="primary"
            >
              Agregar Cuenta
            </Button>
          </Box>

          {formVisible && (
            <Card variant="outlined" style={{ marginBottom: '2rem' }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel>Tipo de Cuenta</InputLabel>
                      <Select
                        value={formData.tipo}
                        onChange={(e) => handleInputChange('tipo', e.target.value)}
                        label="Tipo de Cuenta"
                      >
                        {tipoOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Banco"
                      value={formData.banco}
                      onChange={(e) => handleInputChange('banco', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel>Moneda</InputLabel>
                      <Select
                        value={formData.moneda}
                        onChange={(e) => handleInputChange('moneda', e.target.value)}
                        label="Moneda"
                      >
                        {monedaOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel>Tipo de Clave</InputLabel>
                      <Select
                        value={formData.tipoClaveBancaria}
                        onChange={(e) => handleInputChange('tipoClaveBancaria', e.target.value)}
                        label="Tipo de Clave"
                      >
                        {tipoClaveOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={getClaveDisplay(formData.tipoClaveBancaria)}
                      value={formData.claveBancaria}
                      onChange={(e) => handleInputChange('claveBancaria', e.target.value.replace(/[^0-9]/g, ''))}
                      required
                      error={!!claveBancariaError}
                      helperText={
                        claveBancariaError || `Ingrese su ${getClaveDisplay(formData.tipoClaveBancaria)} (22 dígitos)`
                      }
                      placeholder={`Ingrese su ${getClaveDisplay(formData.tipoClaveBancaria)}`}
                    />
                  </Grid>
                </Grid>
                <Box display="flex" justifyContent="flex-end" gap={1} marginTop={2}>
                  <Button onClick={handleCloseForm} startIcon={<Close />}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveCuenta}
                    variant="contained"
                    disabled={!isFormValid()}
                    startIcon={<Check />}
                    color="primary"
                  >
                    {editingIndex >= 0 ? 'Actualizar' : 'Agregar'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {cuentasBancarias.length === 0 ? (
            <Box textAlign="center" style={{ padding: '2rem' }}>
              <Typography variant="body1" color="textSecondary">
                No hay cuentas bancarias agregadas. Haga clic en "Agregar Cuenta" para comenzar.
              </Typography>
            </Box>
          ) : (
            <List>
              {cuentasBancarias.map((cuenta, index) => (
                <ListItem key={cuenta.id || `${cuenta.tipo}-${cuenta.banco}-${index}`} divider>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold', paddingRight: '10px' }}>
                          {getTipoDisplay(cuenta.tipo)} - {cuenta.banco}
                        </Typography>
                        <Chip label={getMonedaDisplay(cuenta.moneda)} size="small" color="primary" variant="outlined" />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="textSecondary">
                        {getClaveDisplay(cuenta.tipoClaveBancaria)}: {cuenta.claveBancaria}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleOpenForm(index)} style={{ marginRight: '8px' }}>
                      <Edit />
                    </IconButton>
                    <IconButton edge="end" onClick={() => handleDeleteCuenta(index)}>
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          <Box marginTop={2}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving || cuentasBancarias.length === 0}
              fullWidth
            >
              {saving ? <CircularProgress size={20} /> : 'Guardar'}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default SeccionCuentasBancarias;

